import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PBPUTILS } from "../../config/API";
import jsPDF from 'jspdf';
import SuccessPage from '../SuccessPage';

const OffReservation = () => {
  // Get current date and calculate last month
  const getDefaultDate = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    let lastMonth = currentMonth - 1;
    let year = currentYear;

    if (lastMonth < 0) {
      lastMonth = 11;
      year = currentYear - 1;
    }

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return {
      month: monthNames[lastMonth],
      year: year.toString()
    };
  };

  const defaultDate = getDefaultDate();

  // Available pack types
  const packTypes = ['20s', '25s'];

  const [selectedPackType, setSelectedPackType] = useState('20s');
  const [formData, setFormData] = useState({
    otherStampedPacks: {
      '20s': {
        line37: '',
        line38: '',
        line39: '',
        line41: '',
        line42: ''
      },
      '25s': {
        line37: '',
        line38: '',
        line39: '',
        line41: '',
        line42: ''
      }
    }
  });

  const [loading, setLoading] = useState({
    '20s': false,
    '25s': false
  });

  const [submitting, setSubmitting] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);
  const [signatureDataURL, setSignatureDataURL] = useState('');
  const [printedName, setPrintedName] = useState('');
  const [title, setTitle] = useState('');

  // Get taxpayer ID and business name from localStorage
  const [taxpayerId, setTaxpayerId] = useState('');
  const [businessName, setBusinessName] = useState('');

  // Distributor Information State
  const [distributorInfo, setDistributorInfo] = useState({
    distributor: '',
    month: defaultDate.month,
    year: defaultDate.year,
    expirationDate: ''
  });

  // Month options for dropdown
  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Year options
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => (currentYear - 3 + i).toString());

  // Signature pad state and ref
  const sigCanvas = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [currentDate] = useState(new Date().toLocaleDateString());

  // State to track if line37 is editable - only make it read-only when API returns non-zero value
  const [isLine37Editable, setIsLine37Editable] = useState({
    '20s': true,
    '25s': true
  });

  // State to track if the value was manually modified
  const [isLine37ManuallyModified, setIsLine37ManuallyModified] = useState({
    '20s': false,
    '25s': false
  });

  // Get taxpayer ID and business name from localStorage on component mount
  useEffect(() => {
    const storedTaxpayerId = localStorage.getItem("selectedTaxPayer");
    const storedBusinessName = localStorage.getItem("selectedBusinessName");
    const storedExpirationDate = localStorage.getItem("businessLicenseExpirationDate");

    if (storedTaxpayerId) {
      setTaxpayerId(storedTaxpayerId);
    } else {
      setTaxpayerId('109');
    }

    if (storedBusinessName) {
      setBusinessName(storedBusinessName);
      setDistributorInfo(prev => ({
        ...prev,
        distributor: storedBusinessName
      }));
    } else {
      setDistributorInfo(prev => ({
        ...prev,
        distributor: 'Black Canyon Distribution, LLC - PBPN License #41112-7'
      }));
    }

    if (storedExpirationDate) {
      setDistributorInfo(prev => ({
        ...prev,
        expirationDate: storedExpirationDate
      }));
    }
  }, []);

  // Format month for API
  const formatMonthForAPI = (monthName) => {
    const monthMap = {
      'January': '01', 'February': '02', 'March': '03', 'April': '04',
      'May': '05', 'June': '06', 'July': '07', 'August': '08',
      'September': '09', 'October': '10', 'November': '11', 'December': '12'
    };
    return monthMap[monthName] || '01';
  };

  // Fetch starting count from API for line37
  const fetchStartingCount = async (packType) => {
    if (!taxpayerId) return;

    try {
      setLoading(prev => ({ ...prev, [packType]: true }));

      const formattedMonth = formatMonthForAPI(distributorInfo.month);
      const apiUrl = PBPUTILS + `?taxpayer=${taxpayerId}&date=${formattedMonth}-${distributorInfo.year}&reporttype=offres&action=getStartCount&perpack=${packType}`;

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.text();
      const count = parseInt(data.trim(), 10);

      if (!isNaN(count)) {
        setFormData(prev => ({
          ...prev,
          otherStampedPacks: {
            ...prev.otherStampedPacks,
            [packType]: {
              ...prev.otherStampedPacks[packType],
              line37: count.toString()
            }
          }
        }));
        // Only make it read-only if API returns a non-zero value AND it hasn't been manually modified
        if (count !== 0 && !isLine37ManuallyModified[packType]) {
          setIsLine37Editable(prev => ({
            ...prev,
            [packType]: false
          }));
        } else {
          setIsLine37Editable(prev => ({
            ...prev,
            [packType]: true
          }));
        }
      } else {
        setFormData(prev => ({
          ...prev,
          otherStampedPacks: {
            ...prev.otherStampedPacks,
            [packType]: {
              ...prev.otherStampedPacks[packType],
              line37: '0'
            }
          }
        }));
        setIsLine37Editable(prev => ({
          ...prev,
          [packType]: true
        }));
      }
    } catch (error) {
      console.error(`Error fetching starting count for ${packType}:`, error);
      setFormData(prev => ({
        ...prev,
        otherStampedPacks: {
          ...prev.otherStampedPacks,
          [packType]: {
            ...prev.otherStampedPacks[packType],
            line37: '0'
          }
        }
      }));
      setIsLine37Editable(prev => ({
        ...prev,
        [packType]: true
      }));
    } finally {
      setLoading(prev => ({ ...prev, [packType]: false }));
    }
  };

  // Helper function to convert base64 data URL to File object
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  // Add this helper function to convert PDF to Blob
  const pdfToBlob = (pdf) => {
    const pdfOutput = pdf.output('blob');
    return pdfOutput;
  };

  // Generate PDF function with signature and proper labels - accepts optional signature override (use when generating for send, since setState is async)
  const generatePDF = (signatureDataURLOverride) => {
    const pdf = new jsPDF();
    const signatureToUse = signatureDataURLOverride !== undefined ? signatureDataURLOverride : signatureDataURL;
    const currentData = getCurrentPackData();

    const formatAmount = (value) => {
      if (value === null || value === undefined) return '0';
      const s = String(value).replace(/,/g, '').trim();
      if (s === '') return '0';
      const num = Number(s);
      if (isNaN(num)) return String(value);
      if (s.includes('.')) {
        const decimals = s.split('.')[1].length;
        return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      }
      return num.toLocaleString();
    };

    // Set font and colors
    pdf.setFont('helvetica');
    pdf.setTextColor(0, 0, 0);

    // Header Section
    pdf.setFontSize(16);
    pdf.text('Monthly Distributor Report', 105, 20, { align: 'center' });

    pdf.setFontSize(12);
    pdf.text('Prairie Band Potawatomi Tax Commission', 105, 30, { align: 'center' });
    pdf.text('16281 Q Road', 105, 36, { align: 'center' });
    pdf.text('Mayetta, KS 66509', 105, 42, { align: 'center' });

    pdf.setFontSize(10);
    pdf.text('Reports and Payment due on the 10th of each Month', 105, 50, { align: 'center' });

    // Distributor Information
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Distributor Information:', 20, 65);
    pdf.setFontSize(10);
    pdf.text(`Distributor: ${distributorInfo.distributor}`, 20, 72);
    pdf.text(`Month: ${distributorInfo.month} ${distributorInfo.year}`, 20, 78);
    pdf.text(`PBPN Tribal Distributor License Expiration Date: ${distributorInfo.expirationDate}`, 20, 84);

    if (taxpayerId) {
      pdf.text(`Taxpayer ID: ${taxpayerId}`, 20, 90);
    }
    // if (businessName) {
    //   pdf.text(`Business: ${businessName}`, 20, 96);
    // }

    // Pack Type Information
    pdf.text(`Pack Type: ${selectedPackType} packs`, 20, 104);

    // Off-Reservation Stamped Packs Section
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('PBPN/KS Compact Distributor Information - "Other" Off-Reservation STAMPED Packs', 20, 116);

    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    let yPosition = 126;

    // Off-Reservation Stamped Packs Data with proper labels
    const offReservationData = [
      {
        label: '36. Number of cigarettes per pack',
        value: selectedPackType
      },
      {
        label: '37. First of month physical inventory of "Other" STAMPED cigarette packs (should equal line 44 from last month)',
        value: currentData.line37 || '0'
      },
      {
        label: '38. Cigarette packs STAMPED with "Other" Off-Reservation stamps during the month (Line 32)',
        value: currentData.line38 || '0'
      },
      {
        label: '39. "Other" STAMPED cigarette packs returned from "Off-Reservation" retailers',
        value: currentData.line39 || '0'
      },
      {
        label: '40. Total "Other" Off-Reservation STAMPED packs available during the month (Lines 37 + 38 + 39)',
        value: formatNumber(calculateLine40()).toString()
      },
      {
        label: '41. "Other" Off-Reservation STAMPED cigarette packs shipped to Off-Reservation Retailers',
        value: currentData.line41 || '0'
      },
      {
        label: '42. "Other" Off-Reservation STAMPED cigarette packs destroyed or returned to manufacturer',
        value: currentData.line42 || '0'
      },
      {
        label: '43. Total "Other" STAMPED Cigarettes removed from inventory (Line 41 + Line 42)',
        value: formatNumber(calculateLine43()).toString()
      },
      {
        label: '44. End of month physical inventory of "Other" Off-Reservation STAMPED cigarette packs (Line 40 - Line 43)',
        value: formatNumber(calculateLine44()).toString()
      }
    ];

    // Display all data
    offReservationData.forEach((item) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }

      // Split long labels into multiple lines
      const labelLines = pdf.splitTextToSize(item.label, 120);
      const valueLines = pdf.splitTextToSize(formatAmount(item.value), 50);

      const maxLines = Math.max(labelLines.length, valueLines.length);

      for (let i = 0; i < maxLines; i++) {
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = 20;
        }

        if (labelLines[i]) {
          pdf.text(labelLines[i], 20, yPosition);
        }

        if (valueLines[i]) {
          pdf.text(valueLines[i], 180, yPosition, { align: 'right' });
        }

        yPosition += 6;
      }

      // Add extra space after main sections
      if (item.label.includes('44.')) {
        yPosition += 4;
      }
    });

    // Add a new page for signature and certification
    if (yPosition > 180) {
      pdf.addPage();
      yPosition = 20;
    }

    // Signature Section
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Signature and Certification', 105, yPosition, { align: 'center' });
    yPosition += 15;

    // Certification Text
    pdf.setFontSize(10);
    pdf.text('I certify that I am a duly authorized owner, partner, director, officer or', 20, yPosition);
    yPosition += 6;
    pdf.text('representative of the licensee and that the statements made herein are true', 20, yPosition);
    yPosition += 6;
    pdf.text('and correct to the best of my knowledge.', 20, yPosition);
    yPosition += 15;

    // Add signature image if available
    if (signatureToUse) {
      try {
        // Add signature image to PDF
        pdf.addImage(signatureToUse, 'PNG', 20, yPosition, 80, 40);
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Signature:', 20, yPosition - 5);
        yPosition += 50;
      } catch (error) {
        console.error('Error adding signature to PDF:', error);
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Signature: Provided during submission', 20, yPosition);
        yPosition += 10;
      }
    } else {
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Signature: Provided during submission', 20, yPosition);
      yPosition += 10;
    }

    // Printed Name and Title
    pdf.text(`Printed Name: ${printedName}`, 20, yPosition);
    yPosition += 6;
    pdf.text(`Title: ${title}`, 20, yPosition);
    yPosition += 8;

    // Date Signed
    pdf.text(`Date Signed: ${currentDate}`, 20, yPosition);
    yPosition += 15;



    

    return pdf;
  };

  // Function to handle PDF download from SuccessPage
  const handleDownloadPDF = () => {
    const pdf = generatePDF();
    const fileName = `OffReservation_Report_${distributorInfo.month}_${distributorInfo.year}_${selectedPackType}_${taxpayerId || 'unknown'}.pdf`;
    pdf.save(fileName);
  };

  // Function to handle back to form
  const handleBackToForm = () => {
    setShowSuccessScreen(false);
  };

  // Submit form data to API
  // Submit form data to API
  // Submit form data to API



  const submitFormData = async (submissionData) => {
    try {
      setSubmitting(true);

      // Create FormData object
      const formDataToSend = new FormData();

      // Add the form fields
      formDataToSend.append('beginningInventory', submissionData.line37 || '0');
      formDataToSend.append('line38', submissionData.line38 || '0');
      formDataToSend.append('line39', submissionData.line39 || '0');
      formDataToSend.append('line41', submissionData.line41 || '0');
      formDataToSend.append('line42', submissionData.line42 || '0');
      formDataToSend.append('reportMonth', submissionData.reportMonth);
      formDataToSend.append('reportYear', submissionData.reportYear);

      // Add printed name and title
      formDataToSend.append('printedName', printedName);
      formDataToSend.append('title', title);


      // Add signature as blob
      if (submissionData.signature) {
        const signatureFile = dataURLtoFile(submissionData.signature, 'signature.png');
        formDataToSend.append('Signature', signatureFile);
      }

      // Generate PDF with signature passed in (state update is async, so state would still be old here)
      const pdf = generatePDF(submissionData.signature);
      const pdfBlob = pdfToBlob(pdf);
      const pdfFile = new File([pdfBlob], `OffReservation_Report_${distributorInfo.month}_${distributorInfo.year}_${selectedPackType}_${taxpayerId || 'unknown'}.pdf`, {
        type: 'application/pdf'
      });
      formDataToSend.append('PDF', pdfFile);

      // Construct API URL with taxpayer and reporttype
      const apiUrl = PBPUTILS + `?taxpayer=${taxpayerId}&reporttype=offres`;

      console.log('Submitting to:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formDataToSend,
      });

      const responseText = await response.text();
      console.log('API Response:', responseText);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}, message: ${responseText}`);
      }

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.warn('Response is not valid JSON, treating as plain text:', responseText);
        if (responseText.trim() === 'True') {
          return {
            success: true,
            message: 'Report submitted successfully!'
          };
        } else {
          throw new Error(`Unexpected response from server: ${responseText}`);
        }
      }

      if (result && result.Status === 'Success') {
        return {
          success: true,
          message: 'Report submitted successfully!'
        };
      } else if (result && result.Status === 'Error') {
        return {
          success: false,
          message: result.Message,
          duplicateRecord: true
        };

      } else {
        throw new Error('Unexpected response format from server');
      }

    } catch (error) {
      console.error('Error submitting form:', error);

      let errorMessage = 'Failed to submit report. Please try again.';
      if (error.message.includes('Invalid Taxpayer')) {
        errorMessage = 'Invalid Taxpayer ID. Please check your taxpayer information.';
      } else if (error.message.includes('Record already exist')) {
        // Handle duplicate record error from non-JSON response
        const match = error.message.match(/Record already exist for the report type of offres in tax return date (\d+) \/ (\d+) for taxpayer (.+)/);
        if (match) {
          const [, month, year, taxpayerName] = match;
          return {
            success: false,
            message: `A report already exists for ${month}/${year} for taxpayer ${taxpayerName}. Please select a different month or contact support if you need to update an existing report.`,
            duplicateRecord: true
          };
        } else {
          return {
            success: false,
            message: 'A report already exists for this period. Please select a different month or contact support.',
            duplicateRecord: true
          };
        }
      } else if (error.message.includes('HTTP error')) {
        errorMessage = `Server error: ${error.message}`;
      } else if (error.message.includes('Unexpected response')) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch starting count when month, year, pack type, or taxpayer ID changes
  useEffect(() => {
    if (taxpayerId) {
      fetchStartingCount(selectedPackType);
    }
  }, [distributorInfo.month, distributorInfo.year, selectedPackType, taxpayerId]);

  // Initialize canvas size after component mounts
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = document.getElementById('signature-container');
      if (container) {
        const width = container.clientWidth - 32;
        const height = 200;
        setCanvasSize({ width, height });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      otherStampedPacks: {
        ...prev.otherStampedPacks,
        [selectedPackType]: {
          ...prev.otherStampedPacks[selectedPackType],
          [name]: value
        }
      }
    }));

    // Mark line37 as manually modified when user types in it
    if (name === 'line37') {
      setIsLine37ManuallyModified(prev => ({
        ...prev,
        [selectedPackType]: true
      }));
      // Always keep it editable if manually modified
      setIsLine37Editable(prev => ({
        ...prev,
        [selectedPackType]: true
      }));
    }
  };

  // Handle distributor information changes
  const handleDistributorInfoChange = (field, value) => {
    setDistributorInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Get current pack data
  const getCurrentPackData = () => {
    return formData.otherStampedPacks[selectedPackType] || {};
  };

  // OTHER STAMPED calculations
  const calculateLine40 = () => {
    const data = getCurrentPackData();
    const line37 = parseFloat(data.line37) || 0;
    const line38 = parseFloat(data.line38) || 0;
    const line39 = parseFloat(data.line39) || 0;
    return line37 + line38 + line39;
  };

  const calculateLine43 = () => {
    const data = getCurrentPackData();
    const line41 = parseFloat(data.line41) || 0;
    const line42 = parseFloat(data.line42) || 0;
    return line41 + line42;
  };

  const calculateLine44 = () => {
    const line40 = calculateLine40();
    const line43 = calculateLine43();
    return line40 - line43;
  };

  // Format number to avoid NaN display
  const formatNumber = (value) => {
    return isNaN(value) ? 0 : value;
  };

  // Signature functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsSigned(false);
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    // Signature completion logic
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isSigned || (sigCanvas.current && sigCanvas.current.isEmpty())) {
      alert('Please provide a signature before submitting the report.');
      return;
    }

    if (!printedName.trim()) {
      alert('Please enter your printed name.');
      return;
    }

    if (!title.trim()) {
      alert('Please enter your title.');
      return;
    }

    // Get signature data and store it for PDF generation
    const sigDataURL = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
    setSignatureDataURL(sigDataURL);

    const currentData = getCurrentPackData();

    // Prepare submission data
    const submissionData = {
      line37: currentData.line37 || '0',
      line38: currentData.line38 || '0',
      line39: currentData.line39 || '0',
      line41: currentData.line41 || '0',
      line42: currentData.line42 || '0',
      reportMonth: distributorInfo.month,
      reportYear: distributorInfo.year,
      signature: sigDataURL
    };

    console.log('Submitting data:', submissionData);

    // Store submission data for PDF generation
    setSubmissionData({
      distributorInfo,
      currentData,
      selectedPackType,
      taxpayerId,
      businessName,
      printedName,
      title,
      calculations: {
        line40: calculateLine40(),
        line43: calculateLine43(),
        line44: calculateLine44()
      }
    });

    // Submit to API
    const result = await submitFormData(submissionData);

    if (result.success) {
      // Show success screen
      setShowSuccessScreen(true);
    } else {
      if (result.duplicateRecord) {
        alert(`${result.message}`);
      } else {
        alert(`${result.message}`);
      }
    }
  };

  // Prepare report details for SuccessPage
  const reportDetails = {
    'Report Type': 'Off-Reservation Stamped Packs',
    'Pack Type': `${selectedPackType} packs`,
    'Taxpayer ID': taxpayerId,
    'Business': businessName,
    // 'Distributor': distributorInfo.distributor,
    'Period': `${distributorInfo.month} ${distributorInfo.year}`,
    'Submitted': currentDate,

  };

  // If showing success screen, render the SuccessPage component
  if (showSuccessScreen) {
    return (
      <SuccessPage
        title="Off-Reservation Report Submitted Successfully!"
        subtitle={`Your off-reservation stamped packs report for ${selectedPackType} packs has been successfully submitted.`}
        downloadButtonText="Download Off-Reservation Report PDF"
        backButtonText="Back to Home"
        onDownload={handleDownloadPDF}
        onBack={handleBackToForm}
        reportDetails={reportDetails}
        contactInfo={{
          phone: "(785) 966-3060",
          email: "taxcommission@pbpnation.org",
          department: "Prairie Band Potawatomi Tax Commission"
        }}
      >
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-2">Important Notes:</h3>
          <ul className="text-sm text-green-700 space-y-1">

            <li>• The report covers off-reservation stamped packs inventory for the specified period</li>
            <li>• Ensure all stamped pack details are accurately recorded</li>
            <li>• Contact us if you need to make any corrections</li>
          </ul>
        </div>
      </SuccessPage>
    );
  }

  const currentData = getCurrentPackData();

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 bg-white">
      {/* Header Section */}
      <div className="text-center mb-6 sm:mb-8 border-b-2 border-[#840028] pb-4 sm:pb-6">
        <h1 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#840028]">Monthly Distributor Report</h1>
        <div className="font-bold mb-2 text-[#840028] text-sm sm:text-base">
          <div>Prairie Band Potawatomi Tax Commission</div>
          <div>16281 Q Road</div>
          <div>Mayetta, KS 66509</div>
        </div>
        <div className="italic mb-2 text-[#840028] text-sm sm:text-base">
          Reports and Payment due on the 10th of each Month
        </div>
        {taxpayerId && (
          <div className="text-xs sm:text-sm text-gray-600 mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-4">
            <span>Taxpayer ID: <strong>{taxpayerId}</strong></span>
            {businessName && (
              <span>Business: <strong>{businessName}</strong></span>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Distributor Information Section */}
        <div className="border border-gray-300 p-3 sm:p-4 mb-4 sm:mb-6">
          {/* Distributor Row - At Top */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 mb-3">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Distributor:</label>
            <div className="px-3 py-2 border border-gray-300 bg-gray-50 sm:min-w-80 text-sm sm:text-base">
              {distributorInfo.distributor}
            </div>
          </div>

          {/* Month and Year Row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 mb-3">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Month:</label>
            <select
              value={distributorInfo.month}
              onChange={(e) => handleDistributorInfoChange('month', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-32 sm:mr-8 text-sm sm:text-base"
            >
              {monthOptions.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>

            <label className="font-bold sm:w-12 sm:mr-4 text-[#840028] text-sm sm:text-base">Year:</label>
            <select
              value={distributorInfo.year}
              onChange={(e) => handleDistributorInfoChange('year', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-20 text-sm sm:text-base"
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="hidden">
            <input
              type="text"
              value={distributorInfo.reportMonth}
              readOnly
            />
            <input
              type="text"
              value={distributorInfo.reportYear}
              readOnly
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">PBPN Tribal Distributor License Expiration Date:</label>
            <div className="px-3 py-2 border border-gray-300 bg-gray-50 sm:min-w-32 text-sm sm:text-base">
              {distributorInfo.expirationDate}
            </div>
          </div>
        </div>

        {/* Pack Type Selection */}
        <div className="border border-gray-300 p-3 sm:p-4 mb-4 sm:mb-6 bg-blue-50">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Select Pack Type:</label>
            <select
              value={selectedPackType}
              onChange={(e) => setSelectedPackType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-32 text-sm sm:text-base"
            >
              {packTypes.map(packType => (
                <option key={packType} value={packType}>{packType} packs</option>
              ))}
            </select>
            <span className="mt-2 sm:mt-0 sm:ml-4 text-sm text-gray-600 text-center sm:text-left">
              Currently reporting for <strong>{selectedPackType}</strong> packs
            </span>
          </div>
        </div>

        {/* Main Form Content */}
        <div className="p-4 sm:p-6">
          {/* OTHER STAMPED SECTION */}
          <h2 className="text-lg font-semibold mb-4 text-gray-800 border-t pt-6 text-center sm:text-left">
            PBPN/KS Compact Distributor Information - "Other" Off-Reservation STAMPED Packs of Cigarettes in Storage
          </h2>

          {/* Table Header */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="text-sm font-semibold text-gray-700">
              Number of cigarettes per pack --- {selectedPackType}
            </div>
            <div className="text-sm font-semibold text-gray-700 text-center sm:text-right">
              # of Packs {selectedPackType}
            </div>
          </div>

          <div className="space-y-4">
            {/* Line 36 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700">
                36. Number of cigarettes per pack --- {selectedPackType}
              </label>
              <div className="bg-gray-100 px-3 py-2 rounded border text-sm text-center sm:text-right">
                {selectedPackType}
              </div>
            </div>

            {/* Line 37 - Auto-filled from API - Now editable only when API returns non-zero value AND not manually modified */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700">
                37. First of month physical inventory of "Other" STAMPED cigarette packs (should equal line 44 from last month)
                {loading[selectedPackType] && (
                  <span className="ml-2 text-blue-600 text-xs">Loading...</span>
                )}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  name="line37"
                  value={currentData.line37 || ''}
                  onChange={handleInputChange}
                  className={`border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-sm text-right ${!isLine37Editable[selectedPackType] ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  placeholder="0"
                  readOnly={!isLine37Editable[selectedPackType]}
                />
                {loading[selectedPackType] && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Line 38 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700">
                38. Cigarette packs STAMPED with "Other" Off-Reservation stamps during the month (Line 32)
              </label>
              <input
                type="number"
                min="0"
                name="line38"
                value={currentData.line38 || ''}
                onChange={handleInputChange}
                className="border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-sm text-right"
                placeholder="0"
              />
            </div>

            {/* Line 39 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700">
                39. "Other" STAMPED cigarette packs returned from "Off-Reservation" retailers
              </label>
              <input
                type="number"
                min="0"
                name="line39"
                value={currentData.line39 || ''}
                onChange={handleInputChange}
                className="border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-sm text-right"
                placeholder="0"
              />
            </div>

            {/* Line 40 - Calculated */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700 font-semibold">
                40. Total "Other" Off-Reservation STAMPED packs available during the month (Lines 37 + 38 + 39)
              </label>
              <div className="bg-gray-100 px-3 py-2 rounded border text-sm text-center sm:text-right">
                {formatNumber(calculateLine40())}
              </div>
            </div>

            {/* Line 41 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700">
                41. "Other" Off-Reservation STAMPED cigarette packs shipped to Off-Reservation Retailers
              </label>
              <input
                type="number"
                min="0"
                name="line41"
                value={currentData.line41 || ''}
                onChange={handleInputChange}
                className="border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-sm text-right"
                placeholder="0"
              />
            </div>

            {/* Line 42 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700">
                42. "Other" Off-Reservation STAMPED cigarette packs destroyed or returned to manufacturer
              </label>
              <input
                type="number"
                min="0"
                name="line42"
                value={currentData.line42 || ''}
                onChange={handleInputChange}
                className="border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-sm text-right"
                placeholder="0"
              />
            </div>

            {/* Line 43 - Calculated */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <label className="text-sm text-gray-700">
                43. Total "Other" STAMPED Cigarettes removed from inventory (Line 41 + Line 42)
              </label>
              <div className="bg-gray-100 px-3 py-2 rounded border text-sm text-center sm:text-right">
                {formatNumber(calculateLine43())}
              </div>
            </div>

            {/* Line 44 - Calculated */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center border-t pt-4">
              <label className="text-sm text-gray-700 font-semibold">
                44. End of month physical inventory of "Other" Off-Reservation STAMPED cigarette packs (Line 40 - Line 43)
              </label>
              <div className="bg-gray-100 px-3 py-2 rounded border font-semibold text-sm text-center sm:text-right">
                {formatNumber(calculateLine44())}
              </div>
            </div>
          </div>

          {/* Signature Section */}
          <div className="border border-gray-300 p-4 sm:p-6 mt-6 sm:mt-8 rounded-lg">
            <h3 className="text-lg font-bold mb-3 sm:mb-4 text-center text-gray-800">Signature</h3>

            <div className="mb-4">
              <label className="font-bold block mb-2 text-gray-700 text-sm sm:text-base">
                Sign below to certify the accuracy of this report:
              </label>
              <div
                id="signature-container"
                className="border-2 border-gray-300 rounded-lg p-3 sm:p-4 bg-white"
              >
                {canvasSize.width > 0 && (
                  <SignatureCanvas
                    ref={sigCanvas}
                    penColor="#0000FF"
                    backgroundColor="rgb(255, 255, 255)"
                    canvasProps={{
                      width: canvasSize.width,
                      height: canvasSize.height,
                      className: 'signature-canvas w-full border border-gray-400 rounded bg-white cursor-crosshair'
                    }}
                    onBegin={handleSignatureBegin}
                    onEnd={handleSignatureEnd}
                    clearOnResize={false}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <div className="text-sm text-gray-600 text-center sm:text-left">
                {isSigned ? (
                  <span className="text-green-600 font-bold">✓ Signature provided</span>
                ) : (
                  <span className="text-red-600">Please sign above</span>
                )}
              </div>

              <button
                type="button"
                onClick={clearSignature}
                className="px-4 py-2 bg-[#840028] text-white rounded hover:bg-[#6a0020] transition duration-200 text-sm w-full sm:w-auto"
              >
                Clear Signature
              </button>
            </div>

            {/* Printed Name and Title Fields - Added */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Printed Name: <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={printedName}
                    onChange={(e) => setPrintedName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title: <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Enter your title"
                    required
                  />
                </div>
              </div>

              {/* Date Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Signed:
                </label>
                <input
                  type="text"
                  value={currentDate}
                  readOnly
                  className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 font-medium text-gray-700 cursor-not-allowed text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This date is automatically set to today's date and cannot be modified
                </p>
              </div>
            </div>
          </div>

          {/* Certification */}
          <div className="border-t-2 border-b-2 border-[#840028] py-4 sm:py-6 my-6 sm:my-8 text-center italic text-[#840028] text-sm sm:text-base">
            <p>
              I certify that I am a duly authorized owner, partner, director, officer or representative of the licensee and that the statements made herein are true and correct to the best of my knowledge.
            </p>
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              disabled={!isSigned || submitting || !printedName.trim() || !title.trim()}
              className="bg-[#840028] hover:bg-[#6a0020] text-white font-bold py-3 px-6 sm:px-8 rounded-lg transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto text-sm sm:text-base"
            >
              {submitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                isSigned && printedName.trim() && title.trim() ? 'Submit Report' : 'Please Complete All Required Fields'
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Custom CSS for signature canvas */}
      <style jsx>{`
        .signature-canvas {
          cursor: crosshair !important;
          touch-action: none;
        }
        .signature-canvas:active {
          cursor: grabbing !important;
        }
        #signature-container {
          min-height: 200px;
        }
        
        @media (max-width: 640px) {
          #signature-container {
            min-height: 150px;
          }
        }
      `}</style>
    </div>
  );
};

export default OffReservation;